/* eslint-disable no-unused-vars */
import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { decryptData } from "../../utils/crypto";
import apiHandler from "../../handlers/ApiHandler";

export default function useUserData(userID) {
  const queryClient = useQueryClient();

  // Fetch user data
  const fetchUserData = async () => {
    if (!userID) return null;
    console.log("Fetching user data for userID in the hook: ", userID);
    const data = await apiHandler.get(`/api/profile/user?userID=${userID}`);
    return decryptData(data.encryptedData);
  };

  const {
    data: userData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["userData", userID],
    queryFn: fetchUserData,
    enabled: !!userID,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Fetch session data
  const fetchSessionData = async () => {
    if (!userID) return null;
    return await apiHandler.get("/api/profile/user-session-data");
  };

  const {
    data: sessionData,
    isLoading: isSessionLoading,
    error: sessionError,
  } = useQuery({
    queryKey: ["userSessionData", userID],
    queryFn: fetchSessionData,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!userData || !sessionData) return;

    const roles = JSON.parse(sessionData.roles);
    const finalSessionData = {
      user: {
        userID: userData.userID,
        email: userData.email,
        name: userData.name,
        roles,
        loginTime: sessionData.userData?.loginTime || null,
        phone: userData.phone,
        city: userData.city,
        state: userData.state,
        country: userData.country,
      },
      userPreference: sessionData.userPreference || {},
    };

    try {
      sessionStorage.setItem(
        "userFinalSessionData",
        JSON.stringify(finalSessionData)
      );
      const validThemes = ["Light", "Dark", "System"];
      const theme = validThemes.includes(sessionData?.userPreference?.theme)
        ? sessionData.userPreference.theme
        : "Light";
      sessionStorage.setItem("theme", theme);
    } catch (error) {
      console.error("Error saving session data to sessionStorage: ", error);
    }
  }, [userData, sessionData]);

  const updateUserData = useMutation({
    mutationFn: async (updatedData) => {
      const res = await apiHandler.post(
        "/api/profile/update-user-data",
        updatedData
      );
      return decryptData(res.encryptedData);
    },
    onSuccess: (decryptedProfile) => {
      queryClient.setQueryData(["userData", userID], decryptedProfile);
    },
    onError: (error) => {
      console.error("Error updating user data: ", error);
    },
  });

  const updateProfilePicture = useMutation({
    mutationFn: async (formData) => {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await apiHandler.api.post(
        "/api/users/profile-picture/upload",
        formData,
        { withCredentials: true }
      );
      if (!response.data?.encryptedData) return response.data;
      return decryptData(response.data.encryptedData);
    },
    onSuccess: (decryptedProfile) => {
      if (decryptedProfile?.id) {
        queryClient.setQueryData(
          ["userData", decryptedProfile.id],
          decryptedProfile
        );
      } else {
        console.warn("Profile update may have failed silently.");
      }
    },
    onError: (error) => {
      console.error("Error uploading profile picture: ", error);
    },
  });

  const updateUserPreferenceData = useMutation({
    mutationFn: async (updatedPreferenceData) => {
      return await apiHandler.post(
        "/api/profile/update-user-preference-data",
        updatedPreferenceData
      );
    },
    onSuccess: (updatedPreferenceData) => {
      queryClient.setQueryData(["userSessionData", userID], (prevData) => ({
        ...prevData,
        userPreference: updatedPreferenceData,
      }));
    },
    onError: (error) => {
      console.error("Error updating user preference data: ", error);
    },
  });

  return {
    userData,
    isLoading,
    error,
    updateUserData,
    updateProfilePicture,
    sessionData,
    isSessionLoading,
    sessionError,
    updateUserPreferenceData,
  };
}
